import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ProductInfo } from "abweb/dto/product/ProductInfo";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { AbWebServices } from "abweb/service/AbWebServices";
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { PromiseUtils } from "abweb/utils/PromiseUtils";

/**
 * <p>Support lightweight authenticate workflow that aims to get the login dialog displayed as quickly as possible.
 * The authenticate method only returns once the user has been successfully authenticated.  It also can be configured
 * to show a customer-specific splash screen after the user has been authenticated (rarely used).
 *
 * <p>Do NOT put any react or gelato components within this class!
 * Any ui requirements should be handled via pass-in callbacks - as an example,
 * see showLogin, showAccessDeniedDlg, and showCustomSplash callbacks (which could be either gelato or react-backed)
 */
export class AuthenticationWorkflow {
  constructor(alProps, requiredLoginRole,
  /** Show login dialog, and finish only when authentication succeeds.
   * Note that the implementation of showLogin may support preloading */
  showLogin, /** Show access denied dialog */
  showAccessDeniedDlg, showCustomSplash) {
    this.alProps = alProps;
    this.requiredLoginRole = requiredLoginRole;
    this.showLogin = showLogin;
    this.showAccessDeniedDlg = showAccessDeniedDlg;
    this.showCustomSplash = showCustomSplash;
  }

  /**
   * @return false if service initialization failed (but probably throws instead of returning...)
   */
  async authenticate() {
    if (!AbWebServices.isInitialized()) {
      // Defensive!
      throw new Error("Need to initialize all services needed for authentication (AbWebServices, and probably AbReactServices as well)");
    }
    this.alProps.updateBusyMsg(AbWebResources.loadingApplication());
    this.alProps.profileStartup("Before AuthenticationWorkflow::initializeAuthenticationServices (manual login)");
    const success = await this.authenticateWorkflow();
    this.alProps.profileStartup("After AuthenticationWorkflow::initializeAuthenticationServices");
    return success;
  }

  /** Authenticate and configure session according to user credentials */
  async authenticateWorkflow() {
    const prodInfo = await this.fetchProductInfo();
    // When going against an app server, the initial 'index.html' returns the negotiated locale due to
    // the AbInitioLocaleFilter being hit (which determines the negotiated ai_locale cookie).
    // In an nginx environment (or any web server), nginx serves the html, so no locale is negotiated,
    // and thus the above productInfo rpc is the first rpc that returns the negotiated locale.
    // As such, lazily set locale from negotiated locale cookie (it is a no-op if it is the same as previous locale),
    // which will cause resource bundles to reload if it is different locale.
    // See ABSDK-4490 where login dialog had incorrect resources as resource bundles were in the wrong locale.
    await LocaleManager.setLocaleFromCookie();
    let user = await this.fetchCurrentUser(); // stores 'currentUser' in session
    if (prodInfo.usesUserAuthentication) {
      this.alProps.profileStartup("Before AuthenticationWorkflow::appLoginCheck");
      await this.appLoginCheck(prodInfo, user); // launches UI (optionally displaying login first), and fetches logged in user

      // note login may have changed user, so get current user
      user = AbWebServices.sessionData.currentUser;

      // Now that we've passed the login, check to see if the user is allowed to use the product.
      this.alProps.profileStartup("Before AuthenticationWorkflow::checkUserAccess");
      await this.checkUserAccess(user);
    }

    // After user has logged in, get CSRF token to prevent cross-scripting attacks
    await AbWebServices.csrfService().getToken();
    // Now that user is logged in, make sure we're using the user's locale
    // as mhub can retrieve a user's preferred locale from their db and change the ai_locale cookie
    // value.  Note that the user identity is ONLY available after login.
    await LocaleManager.setLocaleFromCookie();
    this.alProps.profileStartup("After AuthenticationWorkflow::authenticatedServicesInitialization");
    await this.customSplashCheck(prodInfo); // launches customer splash if provided

    // Do not need to catch error as caller (BootstrapApp) will gracefully log and render error,
    // and ensure that user is logged out if necessary
    return true;
  }

  ///  Security Lifecycle Methods /////

  async fetchProductInfo() {
    const prodInfo = await AbWebServices.productService.getProductInfo();
    if (prodInfo == null) {
      throw new Error("getProductInfo rpc returned null, so can not authenticate user");
    }
    AbWebServices.sessionData.set("productInfo", prodInfo);
    return prodInfo;
  }
  async fetchCurrentUser() {
    const user = await AbWebServices.authenticationService.getAuthenticatedUser();
    if (user == null) {
      throw new Error("getAuthenticatedUser rpc returned null, so can not authenticate user");
    }
    // user will be anonymous user if there is no currently logged in user (or 403 on getUser rpc)
    AbWebServices.sessionData.set("currentUser", user);
    return user;
  }

  /** Determine whether login prompt needs to be shown, or if can directly show app (user already authenticated).
   * Only invoked if prodInfo.usesUserAuthentication === true */
  appLoginCheck(prodInfo, user) {
    if (!prodInfo.allowGuestAccess && user.isAnonymous) {
      if (user.usesSSO) {
        // CAT-1503: trigger browser url request for a protected resource to initiate app-server-based sso login workflow
        AbWebServices.authenticationService.handleSSORedirect();
        return Promise.resolve();
      } else {
        return this.invokeDlg(this.showLogin);
      }
    }
    return Promise.resolve();
  }

  /**
   * Only invoked if prodInfo.usesUserAuthentication === true
   * @param currentUser
   */
  isUserAllowedAccess(currentUser) {
    return currentUser.roles?.some(userRole => userRole.name === this.requiredLoginRole, this);
  }
  async checkUserAccess(user) {
    if (!this.isUserAllowedAccess(user)) {
      await this.invokeDlg(this.showAccessDeniedDlg); // sets busyIndicator back to true on dlg close

      // Only way they can get out of above dialog is to logout (which reloads the browser tab)
      PromiseUtils.throwAlreadyHandled(AuthenticationWorkflow.LOGOUT_IN_PROGRESS_MSG);
    }
  }
  customSplashCheck(prodInfo) {
    if (prodInfo.getItem(ProductInfo.CUSTOM_SPLASH_DIALOG_INFO) === null) {
      return Promise.resolve();
    }
    this.alProps.profileStartup("Before AuthenticationWorkflow::customSplashScreen");
    return this.invokeDlg(this.showCustomSplash);
  }
  async invokeDlg(showDlg) {
    // Ensure busy indicator not spinning while dialog is displayed
    this.alProps.setBusyIndicatorVisible(false);
    try {
      // Need await so that busy indicator is off for entire time the dialog is open
      return await showDlg();
    } finally {
      this.alProps.setBusyIndicatorVisible(true);
    }
  }
}
_defineProperty(AuthenticationWorkflow, "LOGOUT_IN_PROGRESS_MSG", "Logout in progress");
//# sourceMappingURL=AuthenticationWorkflow.js.map
